import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inchesToCm, cmToInches } from '../helpers/dynamicDimensionHelper';

const ReferenceDimensionModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentWidth, 
  currentHeight, 
  itemType,
  isNewReference = false
}) => {
  const { t } = useTranslation();
  const [unit, setUnit] = useState('inches'); // 'inches' or 'cm'
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [isSelectingText, setIsSelectingText] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Initialize with current dimensions
      if (currentWidth && currentHeight) {
        setWidth(currentWidth);
        setHeight(currentHeight);
      } else {
        // Default dimensions for new reference items
        setWidth('36.0');
        setHeight('82.5');
      }
    }
  }, [isOpen, currentWidth, currentHeight]);

  // Global mouse up handler to catch selections that end outside input fields
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setTimeout(() => {
        setIsSelectingText(false);
      }, 100);
    };

    if (isSelectingText) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isSelectingText]);

  const handleUnitChange = (newUnit) => {
    if (newUnit === unit) return;
    
    // Convert current values to new unit
    if (newUnit === 'cm' && unit === 'inches') {
      setWidth(inchesToCm(width));
      setHeight(inchesToCm(height));
    } else if (newUnit === 'inches' && unit === 'cm') {
      setWidth(cmToInches(width));
      setHeight(cmToInches(height));
    }
    
    setUnit(newUnit);
  };

  const handleSave = () => {
    const parsedWidth = parseFloat(width);
    const parsedHeight = parseFloat(height);
    
    if (isNaN(parsedWidth) || isNaN(parsedHeight) || parsedWidth <= 0 || parsedHeight <= 0) {
      alert(t('modal.invalidDimensions', 'Please enter valid positive numbers for dimensions.'));
      return;
    }
    
    // Convert to inches if needed (system works in inches internally)
    const widthInches = unit === 'cm' ? cmToInches(width) : width;
    const heightInches = unit === 'cm' ? cmToInches(height) : height;
    
    onSave(parseFloat(widthInches), parseFloat(heightInches));
    onClose();
  };

  const handleClose = () => {
    setWidth('');
    setHeight('');
    setUnit('inches');
    onClose();
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    // Don't close modal if user is actively selecting text
    if (isSelectingText) return;
    
    // Only close if clicking directly on the overlay (not on modal content)
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleMouseDown = () => {
    setIsSelectingText(true);
  };

  const handleMouseUp = () => {
    // Use setTimeout to ensure this runs after any click events
    setTimeout(() => {
      setIsSelectingText(false);
    }, 100);
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <div className="modal-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #eee',
          paddingBottom: '16px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            {isNewReference 
              ? t('modal.setAsReference', 'Set as Reference Item')
              : t('modal.editReference', 'Edit Reference Dimensions')
            }
          </h3>
          <button 
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              {isNewReference 
                ? t('modal.newReferenceInfo', 'Enter the real-world dimensions for this item. It will be used to calculate all other dimensions.')
                : t('modal.editReferenceInfo', 'Modify the reference dimensions. All other items will be scaled accordingly.')
              }
            </p>
            
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '12px', 
              borderRadius: '4px',
              marginBottom: '20px',
              border: '1px solid #e9ecef'
            }}>
              <strong style={{ color: '#007bff' }}>
                📐 {t('modal.referenceItem', 'Reference Item')}: {itemType}
              </strong>
            </div>
          </div>

          {/* Unit Toggle */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
              {t('modal.unit', 'Unit')}:
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleUnitChange('inches')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: unit === 'inches' ? '#007bff' : 'white',
                  color: unit === 'inches' ? 'white' : '#333',
                  cursor: 'pointer'
                }}
              >
                {t('modal.inches', 'Inches')}
              </button>
              <button
                onClick={() => handleUnitChange('cm')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: unit === 'cm' ? '#007bff' : 'white',
                  color: unit === 'cm' ? 'white' : '#333',
                  cursor: 'pointer'
                }}
              >
                {t('modal.centimeters', 'Centimeters')}
              </button>
            </div>
          </div>

          {/* Width Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: '#333' }}>
              {t('modal.width', 'Width')} ({unit}):
            </label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              placeholder={`${t('modal.enterWidth', 'Enter width')} (${unit})`}
            />
          </div>

          {/* Height Input */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: '#333' }}>
              {t('modal.height', 'Height')} ({unit}):
            </label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              placeholder={`${t('modal.enterHeight', 'Enter height')} (${unit})`}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '12px',
          borderTop: '1px solid #eee',
          paddingTop: '16px'
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: 'white',
              color: '#333',
              cursor: 'pointer'
            }}
          >
            {t('modal.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#007bff',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            {t('modal.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceDimensionModal;
